import { supabase } from '../lib/supabase';
import puterAI from '../lib/puter';

/**
 * Nova Conversation Service
 * UGC-focused AI assistant for creators
 * Handles: scripting, hooks, brand deals, social media strategy, platform advice
 */

export const novaConversationService = {
  /**
   * Send a message to Nova and get UGC-focused response
   * @param {string} message - User's message
   * @param {Object} context - Current context (page, brand data, etc.)
   * @returns {Promise<Object>} Nova's response with actions
   */
  async sendMessage(message, context = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // Get conversation history
      const conversationHistory = await this.getConversationHistory(user.id);

      // Classify intent (UGC-specific)
      const intent = await this.classifyIntent(message, context);

      // Generate response
      const response = await this.generateResponse(message, intent, context, profile, conversationHistory);

      // Save conversation
      await this.saveMessage(user.id, message, response, context, intent);

      return response;
    } catch (error) {
      console.error('Error in Nova conversation:', error);
      return {
        type: 'error',
        message: "Oops! Nova's having trouble right now. Can you try that again?",
        actions: []
      };
    }
  },

  /**
   * Classify user intent (UGC-focused intents)
   */
  async classifyIntent(message, context) {
    const lowerMessage = message?.toLowerCase()?.trim();

    // Greetings
    if (lowerMessage.match(/^(hi|hey|hello|sup|yo|morning|afternoon)/i)) {
      return { type: 'greeting', action: 'greet', confidence: 1.0 };
    }

    // Scripting help
    if (lowerMessage.match(/(script|hook|opening|cta|call to action|write|generate|create a)/i)) {
      return { type: 'scripting', action: 'help_script', confidence: 0.9 };
    }

    // Hook ideas
    if (lowerMessage.match(/(hook|opener|first line|attention|viral|trending)/i)) {
      return { type: 'hooks', action: 'generate_hooks', confidence: 0.9 };
    }

    // Brand deals
    if (lowerMessage.match(/(brand|deal|sponsor|client|pitch|outreach|rate|pricing|package)/i)) {
      return { type: 'brand_deals', action: 'brand_advice', confidence: 0.85 };
    }

    // Platform-specific advice
    if (lowerMessage.match(/(tiktok|instagram|reels|youtube|shorts|platform|algorithm)/i)) {
      return { type: 'platform', action: 'platform_advice', confidence: 0.85 };
    }

    // Analytics/performance
    if (lowerMessage.match(/(analytics|views|engagement|metrics|performing|boost|improve)/i)) {
      return { type: 'analytics', action: 'analyze_performance', confidence: 0.8 };
    }

    // Content planning
    if (lowerMessage.match(/(content plan|calendar|ideas|what to post|schedule)/i)) {
      return { type: 'planning', action: 'content_plan', confidence: 0.85 };
    }

    // Pricing/packages
    if (lowerMessage.match(/(charge|price|cost|worth|package|rate card)/i)) {
      return { type: 'pricing', action: 'pricing_advice', confidence: 0.85 };
    }

    // General question
    return { type: 'question', action: 'answer_question', confidence: 0.6 };
  },

  /**
   * Generate Nova's response based on intent
   */
  async generateResponse(message, intent, context, profile, history) {
    const userName = profile?.first_name || 'Creator';

    try {
      switch (intent.type) {
        case 'greeting':
          return this.handleGreeting(userName);

        case 'scripting':
          return await this.handleScripting(message, context, userName);

        case 'hooks':
          return await this.handleHooks(message, context, userName);

        case 'brand_deals':
          return await this.handleBrandDeals(message, context, userName);

        case 'platform':
          return await this.handlePlatformAdvice(message, context, userName);

        case 'analytics':
          return this.handleAnalytics(message, context, userName);

        case 'pricing':
          return this.handlePricing(message, context, userName);

        case 'planning':
          return await this.handleContentPlanning(message, context, userName);

        case 'question':
        default:
          return await this.handleGeneralQuestion(message, context, userName, history);
      }
    } catch (error) {
      console.error('Error generating Nova response:', error);
      return {
        type: 'error',
        message: "I'm having trouble with that one. Can you rephrase or try something else?",
        actions: []
      };
    }
  },

  /**
   * Handle greeting
   */
  handleGreeting(userName) {
    const greetings = [
      `Hey ${userName}! 🎬 Ready to create some killer UGC?`,
      `What's up, ${userName}! 👋 What are we working on today?`,
      `Hey there! Need help with scripts, hooks, or brand deals?`,
      `Hi ${userName}! Let's make some content that converts. What's on your mind?`
    ];

    return {
      type: 'greeting',
      message: greetings[Math.floor(Math.random() * greetings.length)],
      actions: [
        { label: 'Generate Script', type: 'navigate', path: '/app/ugc-scripts' },
        { label: 'Find Brands', type: 'navigate', path: '/app/ugc-brands' },
        { label: 'Get Hook Ideas', type: 'focus_input' }
      ]
    };
  },

  /**
   * Handle scripting requests
   */
  async handleScripting(message, context, userName) {
    // Check if we're on scripts page already
    const onScriptsPage = context?.currentPath?.includes('/ugc-scripts');

    if (onScriptsPage) {
      return {
        type: 'scripting',
        message: `Let's build your script! Fill out the form and I'll generate platform-specific hooks, beats, and shot lists for you. Need help with any specific part?`,
        actions: [
          { label: 'Hook Ideas First', type: 'custom', action: 'show_hook_tips' },
          { label: 'CTA Examples', type: 'custom', action: 'show_cta_examples' }
        ]
      };
    }

    return {
      type: 'scripting',
      message: `I can help you write killer scripts! Head to the Scripts page and I'll generate hooks, script beats, and shot lists tailored to your product and platform.`,
      actions: [
        { label: 'Open Script Generator', type: 'navigate', path: '/app/ugc-scripts' },
        { label: 'Quick Hook Tips', type: 'custom', action: 'show_hook_tips' }
      ]
    };
  },

  /**
   * Handle hook requests
   */
  async handleHooks(message, context, userName) {
    const hookTemplates = [
      "POV: You need [product] before your next scroll ends",
      "I tried [product] so you don't have to — here's the real tea",
      "Stop wasting money on [product] dupes — this is the one",
      "[Brand] sent me this — honest review in 30 seconds",
      "3 reasons [product] is my new [platform] cheat code"
    ];

    return {
      type: 'hooks',
      message: `Here are some proven hook templates:\n\n${hookTemplates.map((h, i) => `${i + 1}. ${h}`).join('\n')}\n\nWant me to generate custom hooks for a specific product?`,
      actions: [
        { label: 'Generate Custom Hooks', type: 'navigate', path: '/app/ugc-scripts' },
        { label: 'More Hook Formulas', type: 'custom', action: 'show_advanced_hooks' }
      ]
    };
  },

  /**
   * Handle brand deal questions
   */
  async handleBrandDeals(message, context, userName) {
    return {
      type: 'brand_deals',
      message: `Looking for brand collabs? I can help you:\n• Find brands in our database\n• Write outreach emails\n• Build pricing packages\n\nWhat stage are you at?`,
      actions: [
        { label: 'Browse Brands', type: 'navigate', path: '/app/ugc-brands' },
        { label: 'Pricing Guide', type: 'custom', action: 'show_pricing_guide' },
        { label: 'Outreach Templates', type: 'custom', action: 'show_outreach_templates' }
      ]
    };
  },

  /**
   * Handle platform advice
   */
  async handlePlatformAdvice(message, context, userName) {
    const platform = message.match(/tiktok/i) ? 'TikTok' : 
                    message.match(/instagram|reels/i) ? 'Instagram Reels' :
                    message.match(/youtube|shorts/i) ? 'YouTube Shorts' : 'short-form';

    const advice = {
      'TikTok': '• Keep it 9-15s\n• Front-load hook in first 1-2s\n• Fast cuts, captions, trending audio\n• Use on-screen text',
      'Instagram Reels': '• 15-30s sweet spot\n• Cover safe zones for UI\n• Reuse as Reel + Story\n• Strong first frame',
      'YouTube Shorts': '• 30-45s max\n• Clear VO mix\n• End screen CTA\n• Subscribe prompt'
    };

    return {
      type: 'platform',
      message: `**${platform} Tips:**\n${advice[platform] || advice['TikTok']}\n\nNeed a script optimized for this platform?`,
      actions: [
        { label: 'Generate Platform Script', type: 'navigate', path: '/app/ugc-scripts' },
        { label: 'All Platform Tips', type: 'custom', action: 'show_all_platform_tips' }
      ]
    };
  },

  /**
   * Handle analytics questions
   */
  handleAnalytics(message, context, userName) {
    return {
      type: 'analytics',
      message: `To boost performance:\n\n📊 **Test hooks** - First 3s determine retention\n💡 **Add captions** - 85% watch without sound\n🔥 **Nail the CTA** - Clear next step = higher conversion\n⏱️ **Watch pacing** - Fast cuts keep attention\n\nWhich metric are you trying to improve?`,
      actions: [
        { label: 'Hook Testing Tips', type: 'custom', action: 'hook_ab_testing' },
        { label: 'Analyze My Scripts', type: 'navigate', path: '/app/ugc-scripts' }
      ]
    };
  },

  /**
   * Handle pricing questions
   */
  handlePricing(message, context, userName) {
    return {
      type: 'pricing',
      message: `**UGC Pricing Guide:**\n\n💰 Starter (1 video): $300-500\n💼 Standard (3 videos + raw): $700-1000\n👑 Elite (5 videos + usage): $1200-2000\n\nFactors: follower count, usage rights, deliverables, exclusivity.\n\nWant me to help build your rate card?`,
      actions: [
        { label: 'Build My Packages', type: 'navigate', path: '/app/ugc-scripts' },
        { label: 'Pricing Calculator', type: 'custom', action: 'show_pricing_calc' }
      ]
    };
  },

  /**
   * Handle content planning
   */
  async handleContentPlanning(message, context, userName) {
    return {
      type: 'planning',
      message: `**Content Planning Framework:**\n\n📅 **Batching** - Shoot 3-5 videos/session\n🎯 **Mix:** 60% value, 30% proof, 10% personal\n♻️ **Repurpose** - Same script → 3 platforms\n📊 **Test hooks** - 3 variations per concept\n\nNeed scripts to fill your calendar?`,
      actions: [
        { label: 'Generate Scripts', type: 'navigate', path: '/app/ugc-scripts' },
        { label: 'Content Ideas', type: 'custom', action: 'show_content_ideas' }
      ]
    };
  },

  /**
   * Handle general questions with Puter AI
   */
  async handleGeneralQuestion(message, context, userName, history) {
    try {
      // Check cache first
      const cached = await this.checkQACache(message);
      if (cached) {
        await this.updateCacheHit(cached.id);
        return {
          type: 'answer',
          message: cached.answer,
          actions: cached.actions || []
        };
      }

      // Use Puter AI for dynamic response
      const systemPrompt = `You are Nova, a UGC creator AI coach. You help with:
- Scripting & hooks for TikTok/Reels/Shorts
- Brand deal advice & outreach
- Platform-specific strategy
- Pricing & packages
- Content planning

Be friendly, concise, actionable. Use creator slang naturally. Keep responses under 150 words.`;

      const conversationContext = history.slice(-3).map(msg => 
        `User: ${msg.user_message}\nNova: ${msg.nova_response}`
      ).join('\n\n');

      const fullPrompt = `${systemPrompt}\n\nConversation:\n${conversationContext}\n\nUser: ${message}\n\nNova:`;

      const aiResponse = await puterAI.chat(fullPrompt);
      
      const response = {
        type: 'answer',
        message: aiResponse?.text || "I'm not sure about that one. Can you rephrase?",
        actions: [
          { label: 'Generate Script', type: 'navigate', path: '/app/ugc-scripts' },
          { label: 'Find Brands', type: 'navigate', path: '/app/ugc-brands' }
        ]
      };

      // Cache for future use
      await this.cacheQA(message, response.message, 'question');

      return response;
    } catch (error) {
      console.error('Error generating AI response:', error);
      return {
        type: 'answer',
        message: "Hmm, I'm not sure about that one. Want to try the script generator or brand database?",
        actions: [
          { label: 'Generate Script', type: 'navigate', path: '/app/ugc-scripts' },
          { label: 'Browse Brands', type: 'navigate', path: '/app/ugc-brands' }
        ]
      };
    }
  },

  /**
   * Get conversation history
   */
  async getConversationHistory(userId, limit = 20) {
    try {
      const { data, error } = await supabase
        .from('nova_conversations')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching Nova history:', error);
      return [];
    }
  },

  /**
   * Save message to database
   */
  async saveMessage(userId, userMessage, novaResponse, context, intent) {
    try {
      const { error } = await supabase
        .from('nova_conversations')
        .insert({
          user_id: userId,
          user_message: userMessage,
          nova_response: novaResponse.message,
          intent: intent?.type || 'unknown',
          context: context || {},
          actions: novaResponse.actions || []
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving Nova message:', error);
    }
  },

  /**
   * Check Q&A cache
   */
  async checkQACache(question) {
    try {
      const hash = this.hashQuestion(question);
      const { data, error } = await supabase
        .from('nova_qa_cache')
        .select('*')
        .eq('question_hash', hash)
        .single();

      if (error || !data) return null;
      return data;
    } catch (error) {
      return null;
    }
  },

  /**
   * Cache Q&A pair
   */
  async cacheQA(question, answer, intent) {
    try {
      const hash = this.hashQuestion(question);
      await supabase
        .from('nova_qa_cache')
        .upsert({
          question_hash: hash,
          question,
          answer,
          intent
        }, {
          onConflict: 'question_hash'
        });
    } catch (error) {
      console.error('Error caching Q&A:', error);
    }
  },

  /**
   * Update cache hit count
   */
  async updateCacheHit(cacheId) {
    try {
      await supabase
        .from('nova_qa_cache')
        .update({
          hit_count: supabase.raw('hit_count + 1'),
          last_used_at: new Date().toISOString()
        })
        .eq('id', cacheId);
    } catch (error) {
      console.error('Error updating cache hit:', error);
    }
  },

  /**
   * Hash question for cache lookup
   */
  hashQuestion(question) {
    const normalized = question.toLowerCase().trim().replace(/[^\w\s]/g, '');
    // Simple hash (in production, use crypto.subtle.digest)
    let hash = 0;
    for (let i = 0; i < normalized.length; i++) {
      hash = ((hash << 5) - hash) + normalized.charCodeAt(i);
      hash = hash & hash;
    }
    return hash.toString(36);
  },

  /**
   * Clear conversation history
   */
  async clearHistory(userId) {
    try {
      await supabase
        .from('nova_conversations')
        .delete()
        .eq('user_id', userId);
      return { success: true };
    } catch (error) {
      console.error('Error clearing Nova history:', error);
      return { success: false, error };
    }
  }
};

export default novaConversationService;
